import { KlearDate } from '@klear/date'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { Money, dominantCurrencyTotal, sumByCurrency } from '@klear/money'
import type { CurrencyCode } from '@klear/constants/currency'
import isString from 'lodash/isString'
import mean from 'lodash/mean'
import sortBy from 'lodash/sortBy'

export type OfferForecast = {
  sampleSize: number
  mean: number | null
  median: number | null
  p25: number | null
  p75: number | null
  earlyShare: number | null
  openExposure: Money
  currentOverdueDays: number | null
}

type OfferForecastInput = Pick<
  InvoiceOffer,
  'customerId' | 'endDate' | 'completedEffectiveDate' | 'canReturnAdvance' | 'purchasePrice'
>

function resolveYmd(value: unknown): string | null {
  if (value == null) return null
  if (value instanceof KlearDate) return value.toJSON()
  if (isString(value)) return KlearDate.parse(value)?.toJSON() ?? null
  return null
}

function emptyForecast(openExposure = new Money(), currentOverdueDays: number | null = null): OfferForecast {
  return {
    sampleSize: 0,
    mean: null,
    median: null,
    p25: null,
    p75: null,
    earlyShare: null,
    openExposure,
    currentOverdueDays
  }
}

export function percentile(sortedAscending: readonly number[], p: number): number {
  if (sortedAscending.length === 0) {
    throw new Error('percentile requires a non-empty sample')
  }
  if (sortedAscending.length === 1) return sortedAscending[0]!
  const clamped = Math.min(1, Math.max(0, p))
  const idx = (sortedAscending.length - 1) * clamped
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sortedAscending[lo]!
  const weight = idx - lo
  return sortedAscending[lo]! * (1 - weight) + sortedAscending[hi]! * weight
}

export function offerForecastFromDayOffsets(
  dayOffsets: readonly number[]
): Omit<OfferForecast, 'openExposure' | 'currentOverdueDays'> | null {
  if (dayOffsets.length === 0) return null
  const sorted = sortBy(dayOffsets)
  const earlyCount = dayOffsets.filter((d) => d < 0).length
  return {
    sampleSize: dayOffsets.length,
    mean: mean(dayOffsets),
    median: percentile(sorted, 0.5),
    p25: percentile(sorted, 0.25),
    p75: percentile(sorted, 0.75),
    earlyShare: earlyCount / dayOffsets.length
  }
}

export type OfferForecastOptions = {
  currency?: CurrencyCode
}

export function offerForecastByCustomerId(
  offers: readonly OfferForecastInput[],
  asOf: KlearDate = KlearDate.today(),
  options?: OfferForecastOptions
): Map<number, OfferForecast> {
  const offsetsByCustomerId = new Map<number, number[]>()
  const exposureByCustomerId = new Map<number, Money[]>()
  const overdueByCustomerId = new Map<number, number>()

  for (const offer of offers) {
    const customerId = offer.customerId
    if (customerId == null || customerId <= 0) continue

    const endDate = resolveYmd(offer.endDate)
    const closedAt = resolveYmd(offer.completedEffectiveDate)
    if (endDate != null && closedAt != null) {
      const bucket = offsetsByCustomerId.get(customerId) ?? []
      bucket.push(KlearDate.daysBetween(endDate, closedAt))
      offsetsByCustomerId.set(customerId, bucket)
    }

    if (offer.canReturnAdvance) {
      const exposureBucket = exposureByCustomerId.get(customerId) ?? []
      exposureBucket.push(offer.purchasePrice)
      exposureByCustomerId.set(customerId, exposureBucket)

      if (endDate != null) {
        const overdue = KlearDate.daysBetween(endDate, asOf.toJSON())
        if (overdue > 0) {
          const prev = overdueByCustomerId.get(customerId) ?? 0
          if (overdue > prev) overdueByCustomerId.set(customerId, overdue)
        }
      }
    }
  }

  const customerIds = new Set([...offsetsByCustomerId.keys(), ...exposureByCustomerId.keys()])
  const result = new Map<number, OfferForecast>()
  for (const customerId of customerIds) {
    const historical = offerForecastFromDayOffsets(offsetsByCustomerId.get(customerId) ?? [])
    const exposureTotals = sumByCurrency(exposureByCustomerId.get(customerId) ?? [])
    const scoped = options?.currency
    const openExposure =
      scoped != null ? (exposureTotals.get(scoped) ?? Money.zero(scoped)) : dominantCurrencyTotal(exposureTotals)
    const currentOverdueDays = overdueByCustomerId.get(customerId) ?? null
    if (historical == null) {
      result.set(customerId, emptyForecast(openExposure, currentOverdueDays))
      continue
    }
    result.set(customerId, {
      ...historical,
      openExposure,
      currentOverdueDays
    })
  }
  return result
}

export function offerForecastConfidence(sampleSize: number): 'High' | 'Medium' | 'Low' {
  if (sampleSize >= 10) return 'High'
  if (sampleSize >= 5) return 'Medium'
  return 'Low'
}

export function offerForecastExpectedLabel(median: number | null | undefined): string {
  if (median == null) return '—'
  const days = Math.round(median)
  if (days === 0) return 'On time'
  if (days > 0) return `~${days}d late`
  return `~${Math.abs(days)}d early`
}

export function offerForecastDaysHeadline(median: number | null | undefined): string {
  if (median == null) return '—'
  const days = Math.round(median)
  if (days === 0) return 'On time'
  if (days > 0) return `+${days} days late`
  return `${Math.abs(days)} days early`
}

export function offerForecastLatenessBand(
  median: number | null | undefined
): 'notably early' | 'slightly early' | 'on time' | 'slightly late' | 'moderately late' | 'severely late' | null {
  if (median == null) return null
  const days = Math.round(median)
  if (days <= -7) return 'notably early'
  if (days < 0) return 'slightly early'
  if (days === 0) return 'on time'
  if (days <= 14) return 'slightly late'
  if (days <= 45) return 'moderately late'
  return 'severely late'
}
